import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate }  from 'react-router-dom';
import { useAuth }                 from '../contexts/AuthContext';
import {
  getPartDetail, patchPart, savePart, deletePart, searchParts, mergeIntoPart,
  qrImageUrl, regenerateQr,
  getStatuses, getConditions,
  listImages, uploadImage, deleteImage, setPrimaryImage,
  getLinks, addLink, deleteLink,
  listSuppliers, addSupplier, updateSupplier, removeSupplier, searchAiSuppliers,
  getResearch, generateResearch,
  addNote,
} from '../api/parts';
import './PartDetailPage.css';

/* ── Helpers ─────────────────────────────────────────────────────── */

function linkIcon(url) {
  if (!url) return '🔗';
  const u = url.toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return '▶️';
  if (u.includes('ebay.com'))    return '🛒';
  if (u.includes('amazon.com'))  return '📦';
  if (u.includes('facebook.com') || u.includes('fb.com')) return '👥';
  return '🔗';
}

function statusColor(code) {
  const map = {
    HAVE:      '#50c878', INSTALLED: '#5090c8', ORDERED:  '#C8860A',
    NEED:      '#e05050', MISSING:   '#e05050', SCRAP:    '#6a5a3a',
    SOLD:      '#9060d0', UNKNOWN:   '#4a4040',
  };
  return map[code] || '#6a5a3a';
}

function conditionLabel(code) {
  const map = {
    ORIGINAL: 'Original', NOS: 'NOS', RESTORED: 'Restored',
    USED: 'Used', REPRO: 'Repro', UNKNOWN: 'Unknown',
  };
  return map[code] || code || '—';
}

/* ── Accordion section ───────────────────────────────────────────── */
function Section({ id, title, icon, badge, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`pd-section${open ? ' pd-section-open' : ''}`}>
      <button className="pd-section-hdr" onClick={() => setOpen(o => !o)}>
        <span className="pd-section-icon">{icon}</span>
        <span className="pd-section-title">{title}</span>
        {badge != null && <span className="pd-section-badge">{badge}</span>}
        <span className="pd-section-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="pd-section-body">{children}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
export default function PartDetailPage() {
  const { partId }          = useParams();
  const navigate            = useNavigate();
  const { user, signOut }   = useAuth();
  const fileInputRef        = useRef(null);
  const cameraInputRef      = useRef(null);

  /* ── Core data ────────────────────────────────────────────────── */
  const [part,       setPart]       = useState(null);
  const [statuses,   setStatuses]   = useState([]);
  const [conditions, setConditions] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  /* ── Images ───────────────────────────────────────────────────── */
  const [images,      setImages]      = useState([]);
  const [uploadBusy,  setUploadBusy]  = useState(false);
  const [lightbox,    setLightbox]    = useState(null); // { url, caption }

  /* ── Links ────────────────────────────────────────────────────── */
  const [links,       setLinks]       = useState([]);
  const [newLinkUrl,  setNewLinkUrl]  = useState('');
  const [newLinkDesc, setNewLinkDesc] = useState('');
  const [linkBusy,    setLinkBusy]    = useState(false);

  /* ── Suppliers ────────────────────────────────────────────────── */
  const [suppliers,   setSuppliers]   = useState([]);
  const [showAddSup,  setShowAddSup]  = useState(false);
  const [editSup,     setEditSup]     = useState(null); // supplier being edited
  const [supForm,     setSupForm]     = useState({
    supplier_name: '', supplier_website: '', supplier_phone: '',
    supplier_email: '', listing_url: '', listing_price: '',
    supplier_part_number: '', condition_code: '', notes: '',
  });
  const [supBusy,       setSupBusy]       = useState(false);
  const [supSearchBusy, setSupSearchBusy] = useState(false);
  const [supSearchedAt, setSupSearchedAt] = useState(null);

  /* ── Restoration notes ────────────────────────────────────────── */
  const [notesText,   setNotesText]   = useState('');
  const [notesDirty,  setNotesDirty]  = useState(false);
  const [notesBusy,   setNotesBusy]   = useState(false);
  const [aiResearch,  setAiResearch]  = useState(null);
  const [aiLoading,   setAiLoading]   = useState(false);

  /* ── Attributes edit ──────────────────────────────────────────── */
  const [attrForm,    setAttrForm]    = useState({});
  const [attrDirty,   setAttrDirty]   = useState(false);
  const [attrBusy,    setAttrBusy]    = useState(false);
  const [attrMsg,     setAttrMsg]     = useState('');

  /* ── Notes log ────────────────────────────────────────────────── */
  const [newNote,     setNewNote]     = useState('');
  const [noteBusy,    setNoteBusy]    = useState(false);

  /* ── Quick status/condition ───────────────────────────────────── */
  const [statusBusy, setStatusBusy]   = useState(false);

  /* ── Delete part ──────────────────────────────────────────────── */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBusy,      setDeleteBusy]      = useState(false);
  const [deleteError,     setDeleteError]     = useState('');

  /* ── QR code ─────────────────────────────────────────────────── */
  const [qrBusy,  setQrBusy]  = useState(false);
  const [qrError, setQrError] = useState('');

  /* ── Merge into another part ──────────────────────────────────── */
  const [showMergeModal,  setShowMergeModal]  = useState(false);
  const [mergeStep,       setMergeStep]       = useState('search'); // 'search' | 'confirm'
  const [mergeSearch,     setMergeSearch]     = useState('');
  const [mergeResults,    setMergeResults]    = useState([]);
  const [mergeSearchBusy, setMergeSearchBusy] = useState(false);
  const [mergeTarget,     setMergeTarget]     = useState(null); // the surviving part
  const [mergeBusy,       setMergeBusy]       = useState(false);
  const [mergeError,      setMergeError]      = useState('');

  /* ── Load everything ──────────────────────────────────────────── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detailRes, statusRes, condRes, imgRes, linkRes, supRes] = await Promise.all([
        getPartDetail(partId),
        getStatuses(),
        getConditions(),
        listImages(partId),
        getLinks(partId),
        listSuppliers(partId),
      ]);

      const p = detailRes.data;
      setPart(p);
      setNotesText(p.restoration_notes || '');
      setAttrForm({
        name:                p.name                || '',
        description:         p.description         || '',
        oem_part_number:     p.oem_part_number      || '',
        barcode:             p.barcode              || '',
        quantity:            p.quantity             ?? 1,
        is_primary:          p.is_primary           ? true : false,
        is_date_coded:       p.is_date_coded        ? true : false,
        date_code:           p.date_code            || '',
        is_numbers_matching: p.is_numbers_matching  ? true : false,
        needs_restoration:   p.needs_restoration    ? true : false,
        purchase_price:      p.purchase_price       ?? '',
        estimated_value:     p.estimated_value      ?? '',
        purchase_date:       p.purchase_date        ? p.purchase_date.slice(0,10) : '',
        storage_location:    p.storage_location     || '',
      });

      setStatuses(statusRes.data  || []);
      setConditions(condRes.data  || []);
      setImages(imgRes.data       || []);
      setLinks(linkRes.data       || []);
      setSuppliers(supRes.data    || []);
    } catch (err) {
      setError('Failed to load part: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [partId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── Delete part ──────────────────────────────────────────────── */
  async function handleDeletePart() {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await deletePart(partId);
      // Navigate back to the parts list
      window.history.length > 1 ? navigate(-1) : navigate('/parts');
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Delete failed. Please try again.');
      setDeleteBusy(false);
    }
  }

  /* ── QR handlers ─────────────────────────────────────────────── */
  async function handleRegenerateQr() {
    setQrBusy(true);
    setQrError('');
    try {
      const res = await regenerateQr(partId);
      if (res.success) {
        setPart(p => ({ ...p, qr_code_path: res.data.qr_code_path }));
      }
    } catch (err) {
      setQrError('Could not generate QR code.');
    } finally {
      setQrBusy(false);
    }
  }

  function handlePrintQr() {
    const url = qrImageUrl(part.qr_code_path);
    if (!url) return;
    const win = window.open('', '_blank', 'width=500,height=560');
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>QR — ${part.name}</title>
  <style>
    body { margin: 0; display: flex; flex-direction: column; align-items: center;
           justify-content: center; min-height: 100vh; font-family: sans-serif;
           background: #fff; color: #000; }
    img  { width: 320px; height: 320px; display: block; }
    h2   { font-size: 1rem; margin: 0.75rem 0 0.25rem; text-align: center; max-width: 320px; }
    p    { font-size: 0.75rem; color: #555; margin: 0; }
  </style>
</head>
<body onload="window.print()">
  <img src="${url}" alt="QR Code" />
  <h2>${part.name}</h2>
  ${part.oem_part_number ? `<p>OEM# ${part.oem_part_number}</p>` : ''}
</body>
</html>`);
    win.document.close();
  }

  /* ── Merge modal helpers ──────────────────────────────────────── */
  function openMergeModal() {
    setMergeStep('search');
    setMergeSearch('');
    setMergeResults([]);
    setMergeTarget(null);
    setMergeError('');
    setShowMergeModal(true);
  }

  async function handleMergeSearch(term) {
    setMergeSearch(term);
    if (term.trim().length < 2) { setMergeResults([]); return; }
    setMergeSearchBusy(true);
    try {
      const res = await searchParts(part.vehicleId, term.trim());
      // Exclude the current part and system parts from results
      const filtered = (res.data || []).filter(
        p => p.partId !== parseInt(partId) && !p.is_system
      );
      setMergeResults(filtered);
    } catch {
      setMergeResults([]);
    } finally {
      setMergeSearchBusy(false);
    }
  }

  function selectMergeTarget(targetPart) {
    setMergeTarget(targetPart);
    setMergeStep('confirm');
    setMergeError('');
  }

  async function handleConfirmMerge() {
    if (!mergeTarget) return;
    setMergeBusy(true);
    setMergeError('');
    try {
      // mergeTarget is PRIMARY (survives); current part is SOURCE (deleted)
      await mergeIntoPart(mergeTarget.partId, [parseInt(partId)]);
      // Navigate to the surviving part
      navigate(`/parts/${mergeTarget.partId}`, { replace: true });
    } catch (err) {
      setMergeError(err?.response?.data?.message || 'Merge failed. Please try again.');
      setMergeBusy(false);
    }
  }

  /* ── Quick status change ──────────────────────────────────────── */
  async function handleStatusChange(e) {
    const code = e.target.value;
    setStatusBusy(true);
    try {
      await patchPart(partId, { status_code: code });
      setPart(p => ({ ...p, status_code: code }));
    } catch { /* silent */ }
    finally { setStatusBusy(false); }
  }

  async function handleConditionChange(e) {
    const code = e.target.value;
    setStatusBusy(true);
    try {
      await patchPart(partId, { condition_code: code });
      setPart(p => ({ ...p, condition_code: code }));
    } catch { /* silent */ }
    finally { setStatusBusy(false); }
  }

  /* ── Image upload ─────────────────────────────────────────────── */
  async function handleFileUpload(file) {
    if (!file) return;
    setUploadBusy(true);
    try {
      await uploadImage(partId, file, { is_primary: images.length === 0 });
      const res = await listImages(partId);
      setImages(res.data || []);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current)   fileInputRef.current.value   = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  }

  async function handleDeleteImage(imageId) {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await deleteImage(partId, imageId);
      setImages(imgs => imgs.filter(i => i.partImageId !== imageId));
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.message || err.message));
    }
  }

  async function handleSetPrimary(imageId) {
    try {
      await setPrimaryImage(partId, imageId);
      setImages(imgs => imgs.map(i => ({ ...i, is_primary: i.partImageId === imageId ? 1 : 0 })));
    } catch { /* silent */ }
  }

  /* ── Links ────────────────────────────────────────────────────── */
  async function handleAddLink(e) {
    e.preventDefault();
    if (!newLinkUrl.trim()) return;
    setLinkBusy(true);
    try {
      await addLink(partId, newLinkUrl.trim(), newLinkDesc.trim());
      const res = await getLinks(partId);
      setLinks(res.data || []);
      setNewLinkUrl('');
      setNewLinkDesc('');
    } catch { /* silent */ }
    finally { setLinkBusy(false); }
  }

  async function handleDeleteLink(url) {
    if (!window.confirm('Remove this link?')) return;
    try {
      await deleteLink(partId, url);
      setLinks(ls => ls.filter(l => l.url !== url));
    } catch { /* silent */ }
  }

  /* ── Suppliers ────────────────────────────────────────────────── */
  function openAddSup() {
    setEditSup(null);
    setSupForm({ supplier_name:'', supplier_website:'', supplier_phone:'',
      supplier_email:'', listing_url:'', listing_price:'',
      supplier_part_number:'', condition_code:'', notes:'' });
    setShowAddSup(true);
  }
  function openEditSup(s) {
    setEditSup(s);
    setSupForm({
      supplier_name:        s.supplier_name        || '',
      supplier_website:     s.supplier_website      || '',
      supplier_phone:       s.supplier_phone        || '',
      supplier_email:       s.supplier_email        || '',
      listing_url:          s.listing_url           || '',
      listing_price:        s.listing_price         ?? '',
      supplier_part_number: s.supplier_part_number  || '',
      condition_code:       s.condition_code        || '',
      notes:                s.notes                 || '',
    });
    setShowAddSup(true);
  }

  async function handleSaveSupplier(e) {
    e.preventDefault();
    setSupBusy(true);
    try {
      if (editSup) {
        await updateSupplier(partId, editSup.partSupplierId, supForm);
      } else {
        await addSupplier(partId, supForm);
      }
      const res = await listSuppliers(partId);
      setSuppliers(res.data || []);
      setShowAddSup(false);
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.message || err.message));
    } finally { setSupBusy(false); }
  }

  async function handleRemoveSupplier(partSupplierId) {
    if (!window.confirm('Remove this supplier?')) return;
    try {
      await removeSupplier(partId, partSupplierId);
      setSuppliers(ss => ss.filter(s => s.partSupplierId !== partSupplierId));
    } catch { /* silent */ }
  }

  async function handleAiSupplierSearch() {
    setSupSearchBusy(true);
    try {
      const res = await searchAiSuppliers(partId);
      setSupSearchedAt(res.data?.searched_at || new Date().toISOString());
      // Reload full supplier list so manual + AI entries all appear
      const fresh = await listSuppliers(partId);
      setSuppliers(fresh.data || []);
    } catch (err) {
      alert('Supplier search failed: ' + (err.response?.data?.message || err.message));
    } finally { setSupSearchBusy(false); }
  }

  /* ── Restoration notes ────────────────────────────────────────── */
  async function handleSaveNotes() {
    setNotesBusy(true);
    try {
      await patchPart(partId, { restoration_notes: notesText });
      setPart(p => ({ ...p, restoration_notes: notesText }));
      setNotesDirty(false);
    } catch { /* silent */ }
    finally { setNotesBusy(false); }
  }

  async function handleAiResearch() {
    setAiLoading(true);
    setAiResearch(null);
    try {
      const res = await generateResearch(partId);
      setAiResearch(res.data);
      // Reload to get updated research stored on part
      const detailRes = await getPartDetail(partId);
      setPart(p => ({ ...p, ...detailRes.data }));
    } catch (err) {
      alert('AI research failed: ' + (err.response?.data?.message || err.message));
    } finally { setAiLoading(false); }
  }

  /* ── Attributes ───────────────────────────────────────────────── */
  function attrChange(field, val) {
    setAttrForm(f => ({ ...f, [field]: val }));
    setAttrDirty(true);
    setAttrMsg('');
  }

  async function handleSaveAttr(e) {
    e.preventDefault();
    setAttrBusy(true);
    setAttrMsg('');
    try {
      await savePart(partId, {
        vehicleId:           part.vehicleId,
        refCategoryId:       part.refCategoryId,
        refConditionId:      part.refConditionId,
        refStatusId:         part.refStatusId,
        name:                attrForm.name,
        description:         attrForm.description         || null,
        oem_part_number:     attrForm.oem_part_number     || null,
        barcode:             attrForm.barcode              || null,
        quantity:            parseInt(attrForm.quantity, 10) || 1,
        is_primary:          attrForm.is_primary           ? 1 : 0,
        is_date_coded:       attrForm.is_date_coded        ? 1 : 0,
        date_code:           attrForm.date_code            || null,
        is_numbers_matching: attrForm.is_numbers_matching  ? 1 : 0,
        needs_restoration:   attrForm.needs_restoration    ? 1 : 0,
        purchase_price:      attrForm.purchase_price !== '' ? parseFloat(attrForm.purchase_price) : null,
        estimated_value:     attrForm.estimated_value !== '' ? parseFloat(attrForm.estimated_value) : null,
        purchase_date:       attrForm.purchase_date        || null,
      });
      setAttrDirty(false);
      setAttrMsg('✓ Saved');
      // Refresh part name in header if changed
      setPart(p => ({ ...p, name: attrForm.name }));
      setTimeout(() => setAttrMsg(''), 2500);
    } catch (err) {
      setAttrMsg('Save failed: ' + (err.response?.data?.message || err.message));
    } finally { setAttrBusy(false); }
  }

  /* ── Add note ─────────────────────────────────────────────────── */
  async function handleAddNote(e) {
    e.preventDefault();
    if (!newNote.trim()) return;
    setNoteBusy(true);
    try {
      await addNote(partId, newNote.trim());
      setNewNote('');
      // Reload notes
      const res = await getPartDetail(partId);
      setPart(p => ({ ...p, notes: res.data.notes }));
    } catch { /* silent */ }
    finally { setNoteBusy(false); }
  }

  /* ── Sign out ─────────────────────────────────────────────────── */
  function handleSignOut() {
    signOut();
    localStorage.removeItem('ar_active_vehicle');
    navigate('/');
  }

  /* ── Render ───────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="pd-root">
        <div className="pd-loading">Loading part…</div>
      </div>
    );
  }

  if (error || !part) {
    return (
      <div className="pd-root">
        <div className="pd-error-page">
          <p>{error || 'Part not found.'}</p>
          <button onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/parts')}>← Back</button>
        </div>
      </div>
    );
  }

  const primaryImg = images.find(i => i.is_primary) || images[0];

  return (
    <div className="pd-root">

      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="pd-header">
        <div className="pd-header-left">
          <button className="pd-back-btn" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/parts')}>← Back</button>
          <div className="pd-header-brand">
            <span className="pd-header-gear">⚙</span>
            <span className="pd-header-name">AutoRestoration</span>
          </div>
        </div>
        <div className="pd-header-center">
          <span className="pd-header-part-name">{part.name}</span>
          {part.oem_part_number && (
            <span className="pd-header-oem">#{part.oem_part_number}</span>
          )}
        </div>
        <div className="pd-header-right">
          <button
            className="pd-merge-btn"
            onClick={openMergeModal}
            title="Merge this part into another"
          >
            ⚙️ Merge
          </button>
          <button
            className="pd-delete-btn"
            onClick={() => { setDeleteError(''); setShowDeleteModal(true); }}
            title="Delete this part"
          >
            🗑
          </button>
          <span className="pd-username">@{user?.username}</span>
          <button className="pd-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      {/* ── Status / Condition quick bar ────────────────────────── */}
      <div className="pd-status-bar">
        <div className="pd-status-bar-left">
          <span className="pd-cat-path">
            {part.category}
          </span>
        </div>
        <div className="pd-status-bar-right">
          {/* Condition dropdown */}
          <label className="pd-quick-label">Condition</label>
          <select
            className="pd-quick-select"
            value={part.condition_code || ''}
            onChange={handleConditionChange}
            disabled={statusBusy}
          >
            {conditions.map(c => (
              <option key={c.refConditionId} value={c.code}>{c.name}</option>
            ))}
          </select>

          {/* Status dropdown */}
          <label className="pd-quick-label">Status</label>
          <select
            className="pd-quick-select pd-quick-select-status"
            value={part.status_code || ''}
            onChange={handleStatusChange}
            disabled={statusBusy}
            style={{ color: statusColor(part.status_code), borderColor: statusColor(part.status_code) }}
          >
            {statuses.map(s => (
              <option key={s.refStatusId} value={s.code}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────── */}
      <div className="pd-body">

        {/* ── QR CODE ─────────────────────────────────────────── */}
        <Section id="qr" icon="📱" title="QR Code" defaultOpen={false}>
          <div className="pd-qr-body">
            {part.qr_code_path ? (
              <>
                <img
                  className="pd-qr-img"
                  src={qrImageUrl(part.qr_code_path)}
                  alt={`QR code for ${part.name}`}
                />
                <p className="pd-qr-hint">
                  Scan to open this part on any device. Print it for a physical label.
                </p>
                {qrError && <div className="pd-qr-error">{qrError}</div>}
                <div className="pd-qr-actions">
                  <button className="pd-qr-print-btn" onClick={handlePrintQr}>
                    🖨 Print QR Label
                  </button>
                  <button
                    className="pd-qr-regen-btn"
                    onClick={handleRegenerateQr}
                    disabled={qrBusy}
                  >
                    {qrBusy ? '⏳ Regenerating…' : '↺ Regenerate'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="pd-qr-hint">No QR code yet. Generate one now.</p>
                {qrError && <div className="pd-qr-error">{qrError}</div>}
                <button
                  className="pd-qr-print-btn"
                  onClick={handleRegenerateQr}
                  disabled={qrBusy}
                >
                  {qrBusy ? '⏳ Generating…' : '📱 Generate QR Code'}
                </button>
              </>
            )}
          </div>
        </Section>

        {/* ── PHOTOS ─────────────────────────────────────────── */}
        <Section id="photos" icon="📸" title="Photos"
                 badge={images.length || null} defaultOpen>

          <div className="pd-photo-actions">
            <button className="pd-photo-add-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadBusy}>
              {uploadBusy ? '⏳ Uploading…' : '📁 Add from File'}
            </button>
            <button className="pd-photo-add-btn pd-photo-cam-btn"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={uploadBusy}>
              📷 Take Photo
            </button>
            <input ref={fileInputRef}   type="file" accept="image/*"
                   style={{ display:'none' }}
                   onChange={e => handleFileUpload(e.target.files[0])} />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                   style={{ display:'none' }}
                   onChange={e => handleFileUpload(e.target.files[0])} />
          </div>

          {images.length === 0 ? (
            <div className="pd-photo-empty">No photos yet — add one above.</div>
          ) : (
            <div className="pd-photo-grid">
              {images.map(img => (
                <div key={img.partImageId}
                     className={`pd-photo-card${img.is_primary ? ' pd-photo-primary' : ''}`}>
                  <div className="pd-photo-thumb-wrap"
                       onClick={() => setLightbox({ url: img.url, caption: img.caption })}>
                    {img.url
                      ? <img src={img.url} alt={img.caption || img.original_filename}
                             className="pd-photo-thumb" />
                      : <div className="pd-photo-no-url">🖼</div>
                    }
                    {img.is_primary === 1 && <span className="pd-photo-star">★</span>}
                  </div>
                  {img.caption && <p className="pd-photo-caption">{img.caption}</p>}
                  <div className="pd-photo-btns">
                    {img.is_primary !== 1 && (
                      <button className="pd-photo-btn"
                              onClick={() => handleSetPrimary(img.partImageId)}>
                        ☆ Primary
                      </button>
                    )}
                    <button className="pd-photo-btn pd-photo-del"
                            onClick={() => handleDeleteImage(img.partImageId)}>
                      🗑 Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── RESTORATION NOTES + AI ─────────────────────────── */}
        <Section id="notes" icon="📝" title="Restoration Notes" defaultOpen>
          <div className="pd-notes-wrap">
            <textarea
              className="pd-notes-area"
              value={notesText}
              onChange={e => { setNotesText(e.target.value); setNotesDirty(true); }}
              placeholder="Describe restoration work needed, specifications, history…"
              rows={6}
            />
            <div className="pd-notes-actions">
              <button className="pd-ai-btn"
                      onClick={handleAiResearch}
                      disabled={aiLoading}>
                {aiLoading ? '⏳ Searching…' : '🤖 AI Research Help'}
              </button>
              {notesDirty && (
                <button className="pd-save-btn"
                        onClick={handleSaveNotes}
                        disabled={notesBusy}>
                  {notesBusy ? 'Saving…' : '💾 Save Notes'}
                </button>
              )}
            </div>

            {/* AI Research results */}
            {(part.research || aiResearch) && (
              <AiResearchPanel research={aiResearch || tryParseResearch(part.research)} />
            )}
          </div>
        </Section>

        {/* ── LINKS ──────────────────────────────────────────── */}
        <Section id="links" icon="🔗" title="Links & Resources"
                 badge={links.length || null}>

          <form className="pd-link-form" onSubmit={handleAddLink}>
            <input className="pd-link-input"
                   type="url"
                   placeholder="https://youtube.com/watch?v=…"
                   value={newLinkUrl}
                   onChange={e => setNewLinkUrl(e.target.value)} />
            <input className="pd-link-input"
                   placeholder="Description (optional)"
                   value={newLinkDesc}
                   onChange={e => setNewLinkDesc(e.target.value)} />
            <button className="pd-link-add-btn" type="submit" disabled={linkBusy || !newLinkUrl}>
              + Add
            </button>
          </form>

          {links.length === 0 ? (
            <p className="pd-empty-hint">No links yet.</p>
          ) : (
            <div className="pd-links-list">
              {links.map((lk, i) => (
                <div key={i} className="pd-link-row">
                  <span className="pd-link-icon">{linkIcon(lk.url)}</span>
                  <div className="pd-link-info">
                    <a href={lk.url} target="_blank" rel="noopener noreferrer"
                       className="pd-link-url">
                      {lk.description || lk.url}
                    </a>
                    {lk.description && (
                      <span className="pd-link-domain">{lk.url}</span>
                    )}
                  </div>
                  <div className="pd-link-actions">
                    <button className="pd-link-del"
                            onClick={() => handleDeleteLink(lk.url)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── SUPPLIERS ──────────────────────────────────────── */}
        <Section id="suppliers" icon="🏪" title="Suppliers"
                 badge={suppliers.length || null}>

          {/* Action bar */}
          <div className="pd-sup-toolbar">
            <button className="pd-sup-add-btn" onClick={openAddSup}>+ Add Supplier</button>
            <button className="pd-sup-ai-btn"
                    onClick={handleAiSupplierSearch}
                    disabled={supSearchBusy}>
              {supSearchBusy ? '🔍 Searching…' : '🔍 Search For Part'}
            </button>
          </div>

          {/* Last searched timestamp */}
          {supSearchedAt && (
            <p className="pd-sup-searched-at">
              Last AI search: {new Date(supSearchedAt).toLocaleString()}
            </p>
          )}

          {suppliers.length === 0 ? (
            <p className="pd-empty-hint">No suppliers linked yet. Use Search For Part to find listings.</p>
          ) : (
            <div className="pd-sup-list">
              {suppliers.map(s => (
                <div key={s.partSupplierId} className="pd-sup-card">
                  <div className="pd-sup-card-top">
                    <span className="pd-sup-name">{s.supplier_name}</span>
                    {s.listing_price != null && (
                      <span className="pd-sup-price">
                        ${parseFloat(s.listing_price).toFixed(2)}
                      </span>
                    )}
                  </div>
                  {s.listing_url && (
                    <a href={s.listing_url} target="_blank" rel="noopener noreferrer"
                       className="pd-sup-url">
                      {linkIcon(s.listing_url)} View Listing
                    </a>
                  )}
                  <div className="pd-sup-meta">
                    {s.supplier_part_number && <span>Part#: {s.supplier_part_number}</span>}
                    {s.condition_code       && <span>Cond: {s.condition_code}</span>}
                    {s.supplier_website && (
                      <a href={s.supplier_website} target="_blank" rel="noopener noreferrer">
                        🌐 Website
                      </a>
                    )}
                    {s.supplier_phone && <span>📞 {s.supplier_phone}</span>}
                    {s.updated_at && (
                      <span className="pd-sup-updated">
                        Updated {new Date(s.updated_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {s.notes && <p className="pd-sup-notes">{s.notes}</p>}
                  <div className="pd-sup-actions">
                    <button className="pd-sup-btn" onClick={() => openEditSup(s)}>✏ Edit</button>
                    <button className="pd-sup-btn pd-sup-del"
                            onClick={() => handleRemoveSupplier(s.partSupplierId)}>🗑 Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── PART ATTRIBUTES ────────────────────────────────── */}
        <Section id="attrs" icon="🔧" title="Part Attributes">
          <form className="pd-attr-form" onSubmit={handleSaveAttr}>

            <div className="pd-attr-grid">
              <div className="pd-attr-field">
                <label>Part Name</label>
                <input value={attrForm.name || ''}
                       onChange={e => attrChange('name', e.target.value)} />
              </div>
              <div className="pd-attr-field">
                <label>OEM Part #</label>
                <input value={attrForm.oem_part_number || ''}
                       onChange={e => attrChange('oem_part_number', e.target.value)}
                       placeholder="e.g. 3927186" />
              </div>
              <div className="pd-attr-field">
                <label>Barcode</label>
                <input value={attrForm.barcode || ''}
                       onChange={e => attrChange('barcode', e.target.value)} />
              </div>
              <div className="pd-attr-field">
                <label>Quantity</label>
                <input type="number" min="0" value={attrForm.quantity ?? 1}
                       onChange={e => attrChange('quantity', e.target.value)} />
              </div>
              <div className="pd-attr-field">
                <label>Purchase Price ($)</label>
                <input type="number" step="0.01" min="0"
                       value={attrForm.purchase_price ?? ''}
                       onChange={e => attrChange('purchase_price', e.target.value)}
                       placeholder="0.00" />
              </div>
              <div className="pd-attr-field">
                <label>Estimated Value ($)</label>
                <input type="number" step="0.01" min="0"
                       value={attrForm.estimated_value ?? ''}
                       onChange={e => attrChange('estimated_value', e.target.value)}
                       placeholder="0.00" />
              </div>
              <div className="pd-attr-field">
                <label>Purchase Date</label>
                <input type="date" value={attrForm.purchase_date || ''}
                       onChange={e => attrChange('purchase_date', e.target.value)} />
              </div>
              <div className="pd-attr-field">
                <label>Date Code</label>
                <input value={attrForm.date_code || ''}
                       onChange={e => attrChange('date_code', e.target.value)}
                       placeholder="e.g. H 17" />
              </div>
              <div className="pd-attr-field pd-attr-field-wide">
                <label>Description</label>
                <textarea rows={2} value={attrForm.description || ''}
                          onChange={e => attrChange('description', e.target.value)} />
              </div>
              <div className="pd-attr-field pd-attr-field-wide">
                <label>Storage Location</label>
                <input value={attrForm.storage_location || ''}
                       readOnly
                       placeholder="Set via storage system"
                       className="pd-attr-readonly" />
              </div>
            </div>

            {/* Flag checkboxes */}
            <div className="pd-attr-flags">
              <label className="pd-flag">
                <input type="checkbox"
                       checked={!!attrForm.is_primary}
                       onChange={e => attrChange('is_primary', e.target.checked)} />
                <span>Primary / Numbers-Matching Unit</span>
              </label>
              <label className="pd-flag">
                <input type="checkbox"
                       checked={!!attrForm.is_date_coded}
                       onChange={e => attrChange('is_date_coded', e.target.checked)} />
                <span>Date Coded</span>
              </label>
              <label className="pd-flag">
                <input type="checkbox"
                       checked={!!attrForm.is_numbers_matching}
                       onChange={e => attrChange('is_numbers_matching', e.target.checked)} />
                <span>Numbers Matching</span>
              </label>
              <label className="pd-flag">
                <input type="checkbox"
                       checked={!!attrForm.needs_restoration}
                       onChange={e => attrChange('needs_restoration', e.target.checked)} />
                <span>Needs Restoration</span>
              </label>
            </div>

            <div className="pd-attr-footer">
              {attrMsg && (
                <span className={`pd-attr-msg${attrMsg.startsWith('✓') ? ' ok' : ' err'}`}>
                  {attrMsg}
                </span>
              )}
              <button className="pd-save-btn" type="submit"
                      disabled={attrBusy || !attrDirty}>
                {attrBusy ? 'Saving…' : '💾 Save Attributes'}
              </button>
            </div>
          </form>
        </Section>

        {/* ── NOTES LOG ──────────────────────────────────────── */}
        <Section id="notelog" icon="📋" title="Notes History"
                 badge={(part.notes || []).length || null}>

          <form className="pd-notelog-form" onSubmit={handleAddNote}>
            <input className="pd-notelog-input"
                   value={newNote}
                   onChange={e => setNewNote(e.target.value)}
                   placeholder="Add a note…" />
            <button type="submit" className="pd-save-btn"
                    disabled={noteBusy || !newNote.trim()}>
              + Add
            </button>
          </form>

          {(part.notes || []).length === 0 ? (
            <p className="pd-empty-hint">No notes yet.</p>
          ) : (
            <div className="pd-notelog-list">
              {(part.notes || []).map((n, i) => (
                <div key={i} className="pd-notelog-row">
                  <span className="pd-notelog-date">
                    {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                  </span>
                  <span className="pd-notelog-by">{n.created_by}</span>
                  <span className="pd-notelog-text">{n.note}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── CROSS REFERENCES ───────────────────────────────── */}
        {(part.xrefs || []).length > 0 && (
          <Section id="xrefs" icon="🔄" title="Cross References"
                   badge={part.xrefs.length}>
            <div className="pd-xref-list">
              {part.xrefs.map((x, i) => (
                <div key={i} className="pd-xref-row">
                  <span className="pd-xref-source">{x.source}</span>
                  <span className="pd-xref-num">{x.part_number}</span>
                  {x.notes && <span className="pd-xref-notes">{x.notes}</span>}
                </div>
              ))}
            </div>
          </Section>
        )}

      </div>{/* end pd-body */}

      {/* ── Supplier add/edit modal ─────────────────────────── */}
      {showAddSup && (
        <div className="pd-modal-overlay" onClick={() => setShowAddSup(false)}>
          <div className="pd-modal" onClick={e => e.stopPropagation()}>
            <h3 className="pd-modal-title">
              {editSup ? '✏ Edit Supplier' : '🏪 Add Supplier'}
            </h3>
            <form className="pd-sup-form" onSubmit={handleSaveSupplier}>
              <div className="pd-sup-form-grid">
                <div className="pd-sup-form-field">
                  <label>Supplier Name *</label>
                  <input required value={supForm.supplier_name}
                         onChange={e => setSupForm(f => ({...f, supplier_name: e.target.value}))} />
                </div>
                <div className="pd-sup-form-field">
                  <label>Their Part #</label>
                  <input value={supForm.supplier_part_number}
                         onChange={e => setSupForm(f => ({...f, supplier_part_number: e.target.value}))} />
                </div>
                <div className="pd-sup-form-field">
                  <label>Listing URL</label>
                  <input type="url" value={supForm.listing_url}
                         onChange={e => setSupForm(f => ({...f, listing_url: e.target.value}))}
                         placeholder="https://ebay.com/itm/…" />
                </div>
                <div className="pd-sup-form-field">
                  <label>Listing Price ($)</label>
                  <input type="number" step="0.01" min="0" value={supForm.listing_price}
                         onChange={e => setSupForm(f => ({...f, listing_price: e.target.value}))} />
                </div>
                <div className="pd-sup-form-field">
                  <label>Website</label>
                  <input type="url" value={supForm.supplier_website}
                         onChange={e => setSupForm(f => ({...f, supplier_website: e.target.value}))} />
                </div>
                <div className="pd-sup-form-field">
                  <label>Phone</label>
                  <input value={supForm.supplier_phone}
                         onChange={e => setSupForm(f => ({...f, supplier_phone: e.target.value}))} />
                </div>
                <div className="pd-sup-form-field">
                  <label>Email</label>
                  <input type="email" value={supForm.supplier_email}
                         onChange={e => setSupForm(f => ({...f, supplier_email: e.target.value}))} />
                </div>
                <div className="pd-sup-form-field">
                  <label>Condition</label>
                  <input value={supForm.condition_code}
                         onChange={e => setSupForm(f => ({...f, condition_code: e.target.value}))}
                         placeholder="e.g. NOS, USED" />
                </div>
                <div className="pd-sup-form-field pd-sup-form-wide">
                  <label>Notes</label>
                  <textarea rows={2} value={supForm.notes}
                            onChange={e => setSupForm(f => ({...f, notes: e.target.value}))} />
                </div>
              </div>
              <div className="pd-modal-actions">
                <button type="button" className="pd-modal-cancel"
                        onClick={() => setShowAddSup(false)} disabled={supBusy}>
                  Cancel
                </button>
                <button type="submit" className="pd-modal-confirm" disabled={supBusy}>
                  {supBusy ? 'Saving…' : editSup ? 'Update' : 'Add Supplier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Lightbox ───────────────────────────────────────────── */}
      {lightbox && (
        <div className="pd-lightbox" onClick={() => setLightbox(null)}>
          <div className="pd-lightbox-inner" onClick={e => e.stopPropagation()}>
            <button className="pd-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
            <img src={lightbox.url} alt={lightbox.caption || 'Part photo'}
                 className="pd-lightbox-img" />
            {lightbox.caption && (
              <p className="pd-lightbox-caption">{lightbox.caption}</p>
            )}
          </div>
        </div>
      )}

      {/* ── Merge modal ───────────────────────────────────────────── */}
      {showMergeModal && (
        <div className="pd-modal-overlay" onClick={() => !mergeBusy && setShowMergeModal(false)}>
          <div className="pd-merge-modal" onClick={e => e.stopPropagation()}>

            {/* ── STEP 1: Search ───────────────────────────────── */}
            {mergeStep === 'search' && (
              <>
                <h2 className="pd-merge-title">⚙️ Merge Into Another Part</h2>
                <p className="pd-merge-subtitle">
                  Search for the part this should be merged into. That part will
                  survive; this part (<strong>{part.name}</strong>) will be deleted
                  after its data is absorbed.
                </p>

                <div className="pd-merge-search-row">
                  <input
                    className="pd-merge-search-input"
                    placeholder="Search by name or OEM#…"
                    value={mergeSearch}
                    autoFocus
                    onChange={e => handleMergeSearch(e.target.value)}
                  />
                  {mergeSearchBusy && <span className="pd-merge-searching">⏳</span>}
                </div>

                {mergeSearch.trim().length >= 2 && !mergeSearchBusy && mergeResults.length === 0 && (
                  <div className="pd-merge-empty">No parts found matching "{mergeSearch}"</div>
                )}

                {mergeResults.length > 0 && (
                  <div className="pd-merge-results">
                    {mergeResults.map(p => (
                      <button
                        key={p.partId}
                        className="pd-merge-result-row"
                        onClick={() => selectMergeTarget(p)}
                      >
                        <div className="pd-merge-result-name">{p.name}</div>
                        <div className="pd-merge-result-meta">
                          {p.oem_part_number && <span>OEM# {p.oem_part_number}</span>}
                          {p.category && <span>{p.category}</span>}
                          {p.condition_code && <span>{p.condition_code}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="pd-merge-footer">
                  <button className="pd-merge-cancel-btn" onClick={() => setShowMergeModal(false)}>
                    Cancel
                  </button>
                </div>
              </>
            )}

            {/* ── STEP 2: Confirm ──────────────────────────────── */}
            {mergeStep === 'confirm' && mergeTarget && (
              <>
                <h2 className="pd-merge-title">⚙️ Confirm Merge</h2>

                <div className="pd-merge-parties">
                  <div className="pd-merge-party pd-merge-source">
                    <div className="pd-merge-party-label">SOURCE — will be deleted</div>
                    <div className="pd-merge-party-name">{part.name}</div>
                    {part.oem_part_number && (
                      <div className="pd-merge-party-oem">OEM# {part.oem_part_number}</div>
                    )}
                  </div>
                  <div className="pd-merge-arrow">→</div>
                  <div className="pd-merge-party pd-merge-primary">
                    <div className="pd-merge-party-label">PRIMARY — survives</div>
                    <div className="pd-merge-party-name">{mergeTarget.name}</div>
                    {mergeTarget.oem_part_number && (
                      <div className="pd-merge-party-oem">OEM# {mergeTarget.oem_part_number}</div>
                    )}
                  </div>
                </div>

                <div className="pd-merge-rules">
                  <div className="pd-merge-rule-section">
                    <div className="pd-merge-rule-head">✅ Always moves from source → primary</div>
                    <ul>
                      <li>📸 All photos</li>
                      <li>🏪 All supplier listings (duplicates skipped)</li>
                      <li>📝 All notes and cross-references</li>
                      <li>💳 All transactions</li>
                    </ul>
                  </div>
                  <div className="pd-merge-rule-section">
                    <div className="pd-merge-rule-head">🛡 Primary fields are protected</div>
                    <ul>
                      <li>Name, status, condition stay as-is on primary</li>
                      <li>Empty fields on primary may be filled from source</li>
                      <li>(OEM#, description, prices, restoration notes)</li>
                    </ul>
                  </div>
                </div>

                <p className="pd-merge-warn">
                  <strong>{part.name}</strong> will be permanently deleted after the merge.
                </p>

                {mergeError && <div className="pd-merge-error">{mergeError}</div>}

                <div className="pd-merge-footer">
                  <button
                    className="pd-merge-cancel-btn"
                    onClick={() => setMergeStep('search')}
                    disabled={mergeBusy}
                  >
                    ← Back
                  </button>
                  <button
                    className="pd-merge-confirm-btn"
                    onClick={handleConfirmMerge}
                    disabled={mergeBusy}
                  >
                    {mergeBusy ? '⏳ Merging…' : '⚙️ Merge Now'}
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ──────────────────────────── */}
      {showDeleteModal && (
        <div className="pd-modal-overlay" onClick={() => !deleteBusy && setShowDeleteModal(false)}>
          <div className="pd-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="pd-delete-modal-icon">🗑</div>
            <h2 className="pd-delete-modal-title">Delete Part?</h2>
            <p className="pd-delete-modal-body">
              This will permanently remove <strong>{part.name}</strong> and{' '}
              <strong>all related data</strong> including:
            </p>
            <ul className="pd-delete-modal-list">
              <li>📸 All photos (deleted from storage)</li>
              <li>🏪 All supplier listings</li>
              <li>📝 All notes and cross-references</li>
              <li>🔗 All links and transactions</li>
            </ul>
            <p className="pd-delete-modal-warn">This action cannot be undone.</p>
            {deleteError && (
              <div className="pd-delete-modal-error">{deleteError}</div>
            )}
            <div className="pd-delete-modal-actions">
              <button
                className="pd-delete-modal-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                className="pd-delete-modal-confirm"
                onClick={handleDeletePart}
                disabled={deleteBusy}
              >
                {deleteBusy ? '⏳ Deleting…' : '🗑 Delete Part'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* ── AI Research panel ───────────────────────────────────────────── */
function tryParseResearch(raw) {
  if (!raw || typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

function AiResearchPanel({ research }) {
  if (!research) return null;
  const links  = research.links  || [];
  const issues = research.common_issues || research.known_issues || [];

  return (
    <div className="pd-research-panel">
      <h4 className="pd-research-title">🤖 AI Research</h4>
      {research.summary && (
        <p className="pd-research-summary">{research.summary}</p>
      )}
      {research.oem_specs && (
        <div className="pd-research-block">
          <span className="pd-research-label">OEM Specs</span>
          <p>{research.oem_specs}</p>
        </div>
      )}
      {issues.length > 0 && (
        <div className="pd-research-block">
          <span className="pd-research-label">Common Issues</span>
          <ul className="pd-research-list">
            {issues.map((iss, i) => <li key={i}>{iss}</li>)}
          </ul>
        </div>
      )}
      {links.length > 0 && (
        <div className="pd-research-block">
          <span className="pd-research-label">Helpful Links</span>
          <div className="pd-research-links">
            {links.map((lk, i) => (
              <a key={i} href={lk.url} target="_blank" rel="noopener noreferrer"
                 className="pd-research-link">
                {linkIcon(lk.url)} {lk.title || lk.url}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
